/**
 * useStepSaveGuard.ts - Phantom-update protection for automation step auto-save
 *
 * Step renderers persist on blur (name inputs, rich content editors), which previously
 * queued a PUT even when the user clicked into a field and back out without changing
 * anything. This hook tracks the last-persisted shape of a step and lets save paths
 * skip payloads that are equivalent to what the server already has.
 *
 * Why the baseline is seeded from the first step instance: renderers sync local edits
 * into canvas node data on every keystroke (via `syncStepToNodeData`), so by the time a
 * blur handler fires, the `step` prop already reflects the pending edit. Comparing a save
 * payload against the live `step` prop would therefore suppress real saves. Instead, the
 * baseline starts from the first non-null step (the persisted server state at mount) and
 * advances only when a save is actually queued.
 */

import type * as Api from '@ViewModels';
import { useCallback, useRef } from 'react';
import type { useAutoSave } from './useAutoSave';

type SaveStatus = ReturnType<typeof useAutoSave>['saveStatus'];

/**
 * Step payloads are small API objects (a handful of nested levels), so any structure
 * deeper than this indicates a cycle or corrupt data. Serialization fails open in that
 * case: the save proceeds rather than risking a skipped persist.
 */
const COMPARISON_DEPTH_MAX = 32;

/**
 * Produces a canonical value for equality comparison:
 * - Object keys are sorted so property order never affects the result.
 * - `undefined`, `null`, and `''` are dropped, because save builders coerce absent server
 *   fields to empty strings (e.g. `tagToAddOnClick || ''`) and resending an empty value
 *   for an absent field is a semantic no-op for a full-step PUT.
 * - Objects and arrays that become empty after pruning are dropped for the same reason.
 * Booleans and numbers (including `false` and `0`) are always significant.
 */
function normalizeForComparison(value: unknown, depth: number): unknown {
	if (value === undefined || value === null || value === '') {
		return undefined;
	}
	if (typeof value !== 'object') {
		return value;
	}
	if (depth >= COMPARISON_DEPTH_MAX) {
		throw new Error('Step comparison exceeded maximum depth.');
	}
	if (Array.isArray(value)) {
		// Keep array positions stable: pruning elements would make [null, 'a'] equal ['a'].
		const items = value.map(item => normalizeForComparison(item, depth + 1) ?? null);
		if (items.length > 0) {
			return items;
		} else {
			return undefined;
		}
	}
	const result: Record<string, unknown> = {};
	for (const key of Object.keys(value).sort()) {
		const normalized = normalizeForComparison((value as Record<string, unknown>)[key], depth + 1);
		if (normalized !== undefined) {
			result[key] = normalized;
		}
	}
	if (Object.keys(result).length > 0) {
		return result;
	} else {
		return undefined;
	}
}

/**
 * Serializes a step into a canonical string for equality comparison.
 * Returns null when the step cannot be serialized (cycles, excessive depth);
 * callers must treat null as "not comparable" and allow the save.
 */
export function serializeStepForComparison(step: object): string | null {
	try {
		return JSON.stringify(normalizeForComparison(step, 0) ?? {});
	} catch {
		return null;
	}
}

/**
 * Returns `shouldSaveStep(updatedStep)`, which reports whether a save payload differs
 * from the last-persisted state. Returning true advances the baseline to the new payload,
 * so each distinct payload is persisted exactly once; returning false means the payload
 * is a phantom update and the caller must skip queueing it.
 *
 * While the auto-save status is 'error' the guard always allows the save: the queued data
 * never reached the server, so the user's next interaction must be able to restart the
 * retry cycle (matching pre-guard behavior for failed saves).
 */
export function useStepSaveGuard<TStep extends Api.IAutomationStep>(
	step: TStep | null,
	saveStatus: SaveStatus
): (updatedStep: Partial<TStep>) => boolean {
	const lastPersistedRef = useRef<string | null>(null);

	// Seed from the first step instance we see: node data starts out as the persisted
	// server state and only diverges through user edits, which all pass through this guard.
	if (lastPersistedRef.current === null && step) {
		lastPersistedRef.current = serializeStepForComparison(step);
	}

	// Read the status through a ref so the returned callback stays referentially stable.
	const saveStatusRef = useRef(saveStatus);
	saveStatusRef.current = saveStatus;

	return useCallback((updatedStep: Partial<TStep>): boolean => {
		const serialized = serializeStepForComparison(updatedStep);
		if (saveStatusRef.current === 'error') {
			lastPersistedRef.current = serialized;
			return true;
		}
		if (serialized !== null && serialized === lastPersistedRef.current) {
			return false;
		}
		lastPersistedRef.current = serialized;
		return true;
	}, []);
}
